// @ts-nocheck
/* Starter Sync - StaticListHeader */

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddWishListLineToCart } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import OrderActionSet from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import { formGridProps } from "@store/Themes/common/props/form";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const wishListState = getWishListState(state, state.pages.staticList.wishListId);
    const wishListLines = wishListState.value?.wishListLineCollection;
    const productInfosByWishListLineId = state.pages.staticList.productInfosByWishListLineId;
    return {
        session: getSession(state),
        wishListState,
        productInfosByWishListLineId,
        myListsDetailsPageUrl: getPageLinkByPageType(state, "MyListsDetailsPage")?.url,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        location: getLocation(state),
        canAddAllToCart: wishListLines?.every(o => canAddWishListLineToCart(o, productInfosByWishListLineId)) || false,
    };
};

const mapDispatchToProps = {
    addToWishList,
    addCartLines,
    loadCurrentCart,
};

type Props = WidgetProps &
    HasHistory &
    HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface StaticListActionsStyles {
    errorInfosBox?: InfosBoxProps;
    saveListButton?: ButtonPresentationProps;
    saveListModal?: ModalPresentationProps;
    container?: GridContainerProps;
    listNameGridItem?: GridItemProps;
    listNameTextField?: TextFieldPresentationProps;
    descriptionText?: TypographyPresentationProps;
    addListToCartButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
}

export const staticListActionsStyles: StaticListActionsStyles = {
    errorInfosBox: { type: "error" },
    container: formGridProps.gridContainer,
    listNameGridItem: { width: 12 },
    saveListButton: { variant: "tertiary" },
    saveListModal: { sizeVariant: "small" },
    cancelButton: { variant: "secondary" },
};

const styles = staticListActionsStyles;

const StaticListActions = ({
    session,
    wishListState,
    productInfosByWishListLineId,
    myListsDetailsPageUrl,
    showAddToCartConfirmationDialog,
    signInUrl,
    location,
    canAddAllToCart,
    history,
    toaster,
    addToWishList,
    addCartLines,
    loadCurrentCart,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [listName, setListName] = useState("");
    const [listNameError, setListNameError] = useState("");

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const saveListClickHandler = () => {
        setModalIsOpen(true);
    };

    const listNameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setListName(e.currentTarget.value);
        setListNameError("");
    };

    const saveListButtonClickHandler = () => {
        const wishListLines = wishListState.value?.wishListLineCollection;
        if (!wishListLines || wishListLines.length === 0) {
            return;
        }

        const productInfos = wishListLines.map(o => productInfosByWishListLineId[o.id]!);
        addToWishList({
            newListName: listName,
            productInfos,
            onSuccess: ({ id }) => {
                setModalIsOpen(false);
                if (myListsDetailsPageUrl) {
                    history.push(`${myListsDetailsPageUrl}?id=${id}`);
                }
            },
            onError: errorMessage => {
                setListNameError(errorMessage);
            },
            onComplete(resultProps) {
                if (resultProps.result?.wishList) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.result.wishList);
                } else if (resultProps.result?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    const addListToCartClickHandler = () => {
        const wishListLines = wishListState.value?.wishListLineCollection;
        if (!wishListLines || wishListLines.length === 0) {
            return;
        }

        const productInfos = wishListLines.map(o => productInfosByWishListLineId[o.id]!);
        addCartLines({
            productInfos,
            onSuccess: (cartLinesCollection: CartLineCollectionModel) => {
                loadCurrentCart({ shouldLoadFullCart: true });
                if (showAddToCartConfirmationDialog) {
                    const isQtyAdjusted =
                        cartLinesCollection.cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;

                    toaster.addToast({
                        body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={true} />,
                        messageType: "success",
                    });
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cartLines) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    };

    const wishList = wishListState.value;
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const allQtysIsValid = wishList?.wishListLineCollection?.every(
        o => !!productInfosByWishListLineId[o.id] && productInfosByWishListLineId[o.id]!.qtyOrdered > 0,
    );
    const returnUrl = location.pathname + location.search;

    if (!wishList) {
        return null;
    }

    return (
        <>
            <OrderActionSet
                actions={[
                    {
                        key: "add",
                        label: translate("Add List to Cart"),
                        onClick: addListToCartClickHandler,
                        buttonStyles: styles.addListToCartButton,
                        isDisabled: !allQtysIsValid,
                        isVisible: canAddAllToCart,
                        isSticky: true,
                        testSelector: "staticListModal_AddListToCartButton",
                    },
                    {
                        key: "save",
                        label: translate("Save List"),
                        onClick: saveListClickHandler,
                        buttonStyles: styles.saveListButton,
                        testSelector: "staticListPage_SaveList",
                        isSticky: true,
                    },
                ]}
            />
            <Modal
                {...styles.saveListModal}
                headline={isAuthenticated ? translate("Save List") : translate("Please Sign In")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                {!isAuthenticated && (
                    <InfosBox {...styles.errorInfosBox}>
                        {siteMessage(
                            "Lists_SignIn_Required_To_Save_List_In_Spire",
                            `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`,
                        )}
                    </InfosBox>
                )}
                {isAuthenticated && (
                    <>
                        <GridContainer {...styles.container}>
                            <GridItem {...styles.listNameGridItem}>
                                <Typography {...styles.descriptionText}>
                                    {translate("Enter a name for the new list.")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.listNameGridItem}>
                                <TextField
                                    {...styles.listNameTextField}
                                    label={translate("New List Name")}
                                    labelPosition="top"
                                    value={listName}
                                    error={listNameError}
                                    onChange={listNameChangeHandler}
                                    data-test-selector="staticListModal_ListName"
                                />
                            </GridItem>
                        </GridContainer>
                        <FormActions>
                            <Button
                                {...styles.saveButton}
                                onClick={saveListButtonClickHandler}
                                data-test-selector="staticListModal_SaveList"
                            >
                                {translate("Save List")}
                            </Button>
                            <Button {...styles.cancelButton} onClick={modalCloseHandler}>
                                {translate("Cancel")}
                            </Button>
                        </FormActions>
                    </>
                )}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(StaticListActions))),
    definition: {
        group: "Static List",
        allowedContexts: ["StaticListPage"],
        displayName: "Actions",
    },
};

export default widgetModule;
